import React, { useState, useEffect, useRef } from 'react';
import { Phone, User, Building } from 'lucide-react';
import apiService from '../apiService';

const PhoneNumberInput = ({ 
  value, 
  onChange, 
  onContactSelect, 
  onKeyPress,
  placeholder = "Enter phone number...",
  className = "",
  darkMode = false 
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownDirection, setDropdownDirection] = useState('down'); // 'up' or 'down'
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const theme = {
    bg: darkMode ? 'bg-gray-800' : 'bg-white',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-400' : 'text-gray-600',
    border: darkMode ? 'border-gray-700' : 'border-gray-200',
    hover: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    input: darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900',
    suggestions: darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
  };

  useEffect(() => {
    const searchContacts = async () => {
      if (value && value.length >= 2) {
        setLoading(true);
        try {
          const results = await apiService.searchContacts(value);
          setSuggestions(results || []);
          setShowSuggestions(true);
          
          // Determine dropdown direction based on input position
          if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const spaceBelow = windowHeight - rect.bottom;
            const spaceAbove = rect.top;
            
            // If less than 200px space below, show dropdown above
            setDropdownDirection(spaceBelow < 200 && spaceAbove > 200 ? 'up' : 'down');
          }
        } catch (error) {
          console.error('Contact search error:', error);
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const debounceTimer = setTimeout(searchContacts, 300);
    return () => clearTimeout(debounceTimer);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && 
        !inputRef.current.contains(event.target) &&
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    onChange(e.target.value);
  };

  const handleSuggestionClick = (contact, specificNumber = null) => {
    // Use specific number if provided, otherwise determine which number to use
    let selectedNumber = specificNumber || contact.phone_number;
    
    if (!specificNumber) {
      // If no specific number provided, use the number that matches the search
      if (value === contact.second_number) {
        selectedNumber = contact.second_number;
      } else if (value === contact.internal_number) {
        selectedNumber = contact.internal_number;
      }
    }

    onChange(selectedNumber);
    setShowSuggestions(false);
    
    if (onContactSelect) {
      onContactSelect({ ...contact, selectedNumber });
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          className={`pl-10 pr-4 py-2 w-full rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 outline-none ${className}`}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
              // Recalculate dropdown direction on focus
              if (inputRef.current) {
                const rect = inputRef.current.getBoundingClientRect();
                const windowHeight = window.innerHeight;
                const spaceBelow = windowHeight - rect.bottom;
                const spaceAbove = rect.top;
                setDropdownDirection(spaceBelow < 200 && spaceAbove > 200 ? 'up' : 'down');
              }
            }
          }}
        />
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className={`absolute z-50 w-full ${
            dropdownDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
          } ${theme.suggestions} border rounded-lg shadow-lg max-h-60 overflow-y-auto`}
        >
          {suggestions.map((contact) => {
            // Get all available numbers for this contact
            const availableNumbers = [
              { number: contact.phone_number, label: 'Main', type: 'main' },
              contact.second_number && { number: contact.second_number, label: 'Second', type: 'second' },
              contact.internal_number && { number: contact.internal_number, label: 'Internal', type: 'internal' }
            ].filter(Boolean);

            return (
              <div key={contact.id} className={`border-b last:border-b-0 ${theme.border}`}>
                {/* Contact Header */}
                <div className={`p-3 ${theme.hover} cursor-pointer`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-blue-500" />
                        <span className={`font-medium ${theme.text}`}>
                          {contact.full_name || 'Unknown'}
                        </span>
                      </div>
                      
                      {contact.company && (
                        <div className="flex items-center gap-2 mt-1">
                          <Building className="w-3 h-3 text-gray-400" />
                          <span className={`text-sm ${theme.textSecondary}`}>
                            {contact.company}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Number Options */}
                <div className="px-3 pb-2">
                  {availableNumbers.map((numberInfo, index) => (
                    <div
                      key={index}
                      onClick={() => handleSuggestionClick(contact, numberInfo.number)}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${theme.hover} mb-1 last:mb-0`}
                    >
                      <div className="flex items-center gap-2">
                        <Phone className="w-3 h-3 text-gray-400" />
                        <span className={`text-sm font-mono ${theme.text}`}>
                          {numberInfo.number}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          numberInfo.type === 'main' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                          numberInfo.type === 'second' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                          'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                        }`}>
                          {numberInfo.label}
                        </span>
                      </div>
                      
                      {/* Highlight matching number */}
                      {value && numberInfo.number.includes(value) && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PhoneNumberInput;